import React, { useCallback, useMemo, useState } from 'react';
import classnames from 'clsx';

import { InputAdornmentPosition } from 'ui-components/components/number-input/input-adornment-position';

import { ArrowButtonGroup } from './arrow-button-group';
import style from './style.module.less';
import { clamp } from '../../services/math';
import { generateDataTestidAttribute } from '../../services/testid-service';

/**
 * @typedef {import('./number-input.types.d.ts').NumberInputComponent} NumberInputComponent
 * @typedef {import('./number-input.types.d.ts').InputAdornmentPositionValues} InputAdornmentPositionValues
 */

/**
 * @param {number | null} a
 * @param {number} b
 * @returns {number}
 */
const addNumber = (a, b) => {
  const aNumber = Number.parseFloat(String(a || 0));
  const bNumber = Number.parseFloat(String(b || 0));
  const ab = aNumber + bNumber;
  return Number.parseFloat(String(ab));
};

/**
 * @param {string | number | null} [value]
 * @returns {number | undefined}
 */
const fromInputValue = (value) => {
  return !!value || value === '0' || value === 0 ? Number.parseFloat(String(value)) : undefined;
};

/**
 * @param {number | null} [value]
 * @returns {string}
 */
const toInputValue = (value) => {
  return value || value === 0 ? `${value}` : '';
};

/**
 * @param {React.MouseEvent} e
 */
const stopPropagation = (e) => {
  e.stopPropagation();
};

/**
 * @type {NumberInputComponent}
 */
export const NumberInput = ({
  placeholder = null,
  className = null,
  disabled = false,
  shrinkable = false,
  testId = null,
  defaultValue = 0,
  onArrowButtonPress = null,
  onChange = null,
  min = null,
  max = null,
  value = null,
  onFocus = null,
  onBlur = null,
  onKeyDown = null,
  label = null,
  adornment,
  adornmentPosition = InputAdornmentPosition.Start,
  id,
  labelId,
  shouldDisableOnMinOrMax = false,
  disabledDecrementTooltipMessage = null,
  disabledIncrementTooltipMessage = null,
}) => {
  const [hasFocus, setHasFocus] = useState(false);
  const [hasChanged, setHasChanged] = useState(false);

  const shouldDisableIncrementOnly = shouldDisableOnMinOrMax && value === max;
  const shouldDisableDecrementOnly = shouldDisableOnMinOrMax && value === min;

  const internalValue = useMemo(() => {
    if (!hasChanged && hasFocus && value === defaultValue) {
      return null;
    }
    return value;
  }, [defaultValue, hasFocus, hasChanged, value]);

  const clampValue = useCallback(
    (v) => {
      return clamp(v, min, max);
    },
    [min, max],
  );

  const change = useCallback(
    (v) => {
      const newValue = clampValue(v);
      onChange(newValue);
      setHasChanged(true);
    },
    [clampValue, onChange],
  );

  const onIncrement = useCallback(() => {
    const oneMore = clampValue(addNumber(internalValue, 1));
    setHasChanged(true);
    if (onArrowButtonPress) {
      onArrowButtonPress(oneMore);
    } else {
      onChange(oneMore);
    }
  }, [internalValue, onArrowButtonPress, onChange, clampValue]);

  const onDecrement = useCallback(() => {
    const oneLess = clampValue(addNumber(internalValue, -1));
    setHasChanged(true);
    if (onArrowButtonPress) {
      onArrowButtonPress(oneLess);
    } else {
      onChange(oneLess);
    }
  }, [internalValue, onArrowButtonPress, onChange, clampValue]);

  const onInputChange = useCallback(
    (event) => {
      change(fromInputValue(event.target.value));
    },
    [change],
  );

  const internalOnFocus = useCallback(
    (e) => {
      setHasFocus(true);
      if (onFocus) {
        onFocus(e);
      }
    },
    [setHasFocus, onFocus],
  );

  const internalOnBlur = useCallback(
    (e) => {
      setHasFocus(false);

      // Make sure value respects min when losing focus
      if (min !== null && (value === undefined || value === null || value < min)) {
        change(min);
      }

      if (onBlur) {
        onBlur(e);
      }
    },
    [setHasFocus, onBlur, min, value, change],
  );

  const internalOnKeyDown = useCallback(
    (e) => {
      setHasFocus(false);
      if (onKeyDown) {
        onKeyDown(e);
      }
    },
    [setHasFocus, onKeyDown],
  );

  const classes = classnames(className, style['number-input'], {
    [style.focused]: hasFocus,
    [style.disabled]: disabled,
    [style.shrinkable]: shrinkable,
  });

  return (
    <div {...generateDataTestidAttribute('number-input')} className={classes} onClick={stopPropagation}>
      {adornmentPosition === InputAdornmentPosition.Start && adornment}
      <input
        aria-label={label}
        aria-labelledby={labelId}
        disabled={disabled}
        id={id}
        onBlur={internalOnBlur}
        onChange={onInputChange}
        onFocus={internalOnFocus}
        onKeyDown={internalOnKeyDown}
        placeholder={placeholder}
        type='number'
        value={toInputValue(internalValue)}
        {...generateDataTestidAttribute(testId)}
      />
      {adornmentPosition === InputAdornmentPosition.End && adornment}
      <ArrowButtonGroup
        decrement={onDecrement}
        disabled={disabled}
        disabledDecrementTooltipMessage={disabledDecrementTooltipMessage}
        disabledIncrementTooltipMessage={disabledIncrementTooltipMessage}
        increment={onIncrement}
        shouldDisableDecrementOnly={shouldDisableDecrementOnly}
        shouldDisableIncrementOnly={shouldDisableIncrementOnly}
      />
    </div>
  );
};
